import {Component, Input, OnDestroy} from '@angular/core';
import {Subscription} from 'rxjs';
import {
    WeiiCalculationOptionAvailability,
    WeiiCalculationOptionCoverage,
    WeiiCalculationOptionCoverageMap,
    WeiiCalculationOptionId,
    WeiiCalculationOptionsState,
    WeiiCalculationService
} from '../../../utils/weii-calculation.service';

interface WeiiCalculationOptionDefinition {
    id: WeiiCalculationOptionId;
    label: string;
    description: string;
    unavailableMessage: string;
}

const OPTION_DEFINITIONS: WeiiCalculationOptionDefinition[] = [
    {
        id: WeiiCalculationOptionId.ExcludeEvCharging,
        label: 'Exclude EV charging consumption',
        description: 'Refine the WEii score by excluding separately metered EV charging consumption from the building performance. This provides a more representative view of the building\'s energy efficiency.',
        unavailableMessage: 'Not available. This calculation requires measured EV charging consumption.'
    },
    {
        id: WeiiCalculationOptionId.IncludeOnsiteGeneration,
        label: 'Include onsite generation',
        description: 'Refine the WEii score by including locally generated and directly consumed electricity in the calculation. This provides a more representative view of the building\'s actual energy use. This option is only available when complete and reliable gross production data is available.',
        unavailableMessage: 'Not available. This calculation requires complete and reliable gross production data.'
    },
    {
        id: WeiiCalculationOptionId.NormalizeClimateYear,
        label: 'Normalize climate year',
        description: 'Refine the WEii score by normalizing climate-sensitive gas and heat consumption to the WEii 3.1 reference climate of 2,200 heating degree days. The calculation uses a 16.5 °C base temperature and assumes that 80% of gas and heat consumption is used for space heating. This allows buildings and different years to be compared more fairly. Enable this option only when that assumption is appropriate for the selected buildings. Only complete calculation years are normalized; provisional years continue to show actual consumption.',
        unavailableMessage: 'Not available. The required climate data is missing for this calculation.'
    }
];

@Component({
    selector: 'app-weii-calculation-options',
    templateUrl: './weii-calculation-options.component.html',
    styleUrls: ['./weii-calculation-options.component.scss']
})
export class WeiiCalculationOptionsComponent implements OnDestroy {
    @Input() availability: Partial<WeiiCalculationOptionAvailability> = {};
    @Input() coverage: Partial<WeiiCalculationOptionCoverageMap> = {};
    @Input() isFullElectric = false;
    @Input() inputIdPrefix = 'weii-calculation-option';
    @Input() showTitle = true;

    readonly optionDefinitions = OPTION_DEFINITIONS;
    optionState: WeiiCalculationOptionsState;
    private readonly optionsSubscription: Subscription;

    constructor(private readonly calculationService: WeiiCalculationService) {
        this.optionState = calculationService.options;
        this.optionsSubscription = calculationService.options$.subscribe(options => this.optionState = options);
    }

    ngOnDestroy(): void {
        this.optionsSubscription.unsubscribe();
    }

    optionId(option: WeiiCalculationOptionDefinition): string {
        return `${this.inputIdPrefix}-${option.id}`;
    }

    isAvailable(option: WeiiCalculationOptionDefinition): boolean {
        return this.availability[option.id] === true;
    }

    isChecked(option: WeiiCalculationOptionDefinition): boolean {
        return this.isAvailable(option) && this.optionState[option.id];
    }

    optionCoverage(option: WeiiCalculationOptionDefinition): WeiiCalculationOptionCoverage | null {
        const coverage = this.coverage[option.id];
        return coverage?.total > 1 ? coverage : null;
    }

    unavailableMessage(option: WeiiCalculationOptionDefinition): string {
        return this.isFullElectric && option.id === WeiiCalculationOptionId.NormalizeClimateYear
            ? 'Not available. Climate normalization is not possible for a Full Electric building.'
            : option.unavailableMessage;
    }

    setOption(option: WeiiCalculationOptionDefinition, enabled: boolean): void {
        if (this.isAvailable(option)) {
            this.calculationService.setOptionEnabled(option.id, enabled);
        }
    }
}
